import {
  Badge,
  Box,
  Button,
  Container,
  FormField,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { LotStatus } from '@parallel-fluidics/shared/constants';
import { allocateBomConsumption } from '@parallel-fluidics/shared/models/lots';
import PPartRefBadge from 'components/Projects/pPartRefBadge';
import fetchLot from 'features/lots/api/fetchLot';
import useLotsByIds from 'features/lots/hooks/useLotsByIds';
import useGetProjectPartByDocId from 'features/pPart/hooks/useGetProjectPartByDocId';
import ScanField from 'features/scan/components/ScanField';
import { ScanTypes } from 'features/scan/utils/parseScanCode';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { toast } from 'sonner';

/**
 * The Components checklist on a traveler step. Self-contained: fetches
 * the step's own part (cached — PPartDisplay warms the same query) and
 * renders nothing when it has no BOM. Otherwise: one row per line with
 * coverage (saved installs locked, pending scans removable until save)
 * and one scan field that auto-assigns bags to their matching line.
 * Coverage is reported up through onCoverageChange for the Passed gate,
 * form validation, and the save manifest. onPendingChange is called with
 * an updater function (prev => next) so the parent can apply it against
 * its latest state — scans land after an async fetch, and a plain array
 * would carry a stale snapshot.
 */
export default function StepComponentsField({
  stepIndex,
  projectDocId = null,
  pPartDocId = null,
  installedLots = [],
  pendingLots,
  onPendingChange,
  onCoverageChange,
}) {
  const { data: part } = useGetProjectPartByDocId({
    projectDocId, pPartDocId,
  });
  const bom = useMemo(() => part?.components || [], [part]);

  const savedLotIds = useMemo(
    () => installedLots.map((entry) => entry.lotId),
    [installedLots],
  );
  const { data: savedLots } = useLotsByIds(savedLotIds);
  const savedLotById = useMemo(() => Object.fromEntries(
    (savedLots || []).map((lot) => [lot.id, lot]),
  ), [savedLots]);

  // Saved installs exist but their lots haven't resolved yet — coverage
  // can't be computed (an empty savedLotById would count savedQty as 0,
  // making residualLines the full BOM and letting a save consume it again).
  const savedLotsLoading = savedLotIds.length > 0 && !savedLots;

  // Per-line coverage: what saved installs already provide, plus a live
  // allocation of the pending (unsaved) scans.
  const coverage = useMemo(() => {
    // Fail closed until the part doc and the saved bags are actually in
    // hand: the queries may still be loading, or disabled while the
    // traveler's project name resolves to its doc ID. A step with a part
    // must never report "no BOM" (or a full-BOM residual) just because
    // we can't know yet.
    if ((pPartDocId && !part) || savedLotsLoading) {
      return {
        lines: [], deltaByLotId: {}, residualLines: [], covered: false, loading: true,
      };
    }
    if (!bom.length) return null;
    const residualLines = bom.map((line) => {
      const savedQty = installedLots.reduce((sum, entry) => {
        const lot = savedLotById[entry.lotId];
        return lot && lot.projectDocId === line.projectDocId
          && lot.pPartDocId === line.pPartDocId
          ? sum + (entry.quantity || 0) : sum;
      }, 0);
      return {
        projectDocId: line.projectDocId,
        pPartDocId: line.pPartDocId,
        quantity: Math.max(line.quantity - savedQty, 0),
        fullQuantity: line.quantity,
      };
    });
    const { lines, deltaByLotId } = allocateBomConsumption(1, residualLines, pendingLots);
    const mergedLines = lines.map((line, i) => ({
      ...line, fullQuantity: residualLines[i].fullQuantity,
    }));
    return {
      lines: mergedLines,
      deltaByLotId,
      residualLines,
      covered: mergedLines.every((line) => line.short === 0),
    };
  }, [bom, part, pPartDocId, savedLotsLoading, installedLots, savedLotById, pendingLots]);

  // Report only what the parent consumes (the Passed gate and the save
  // manifest: covered + residualLines), memoized by content so the fresh
  // object identities the coverage useMemo produces each render can't
  // re-fire the effect — its deps stay honest, no lint suppression.
  const signature = JSON.stringify(coverage && {
    covered: coverage.covered,
    residualLines: coverage.residualLines,
  });
  const report = useMemo(() => JSON.parse(signature), [signature]);
  useEffect(() => {
    onCoverageChange(stepIndex, report);
  }, [onCoverageChange, report, stepIndex]);

  if (!coverage || coverage.loading) return null;

  const handleScan = async ({ id }) => {
    if (installedLots.some((entry) => entry.lotId === id)
      || pendingLots.some((lot) => lot.id === id)) {
      toast.warning('That bag is already scanned on this step');
      return;
    }
    try {
      const lot = await fetchLot(id);
      if ([LotStatus.QUARANTINED, LotStatus.AWAITING_INSPECTION].includes(lot.status)) {
        toast.error(`${lot.code} is ${lot.status} and can't be consumed`);
        return;
      }
      const onBom = bom.some((line) => line.projectDocId === lot.projectDocId
        && line.pPartDocId === lot.pPartDocId);
      if (!onBom) {
        toast.error(`${lot.code} isn't on this step's BOM`);
        return;
      }
      if (lot.status === LotStatus.EMPTY) {
        toast.warning(`${lot.code} is marked Empty — its count may be off`);
      }
      // Updater form, with the duplicate re-check inside: fetchLot is async,
      // so this list may have changed since the fast-path check above.
      onPendingChange((prev) => (prev.some((pendingLot) => pendingLot.id === id)
        ? prev
        : [...prev, lot]));
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Container>
      <FormField
        label="Components"
        description="Scan the bag used for each BOM line. Bags are consumed when the traveler is saved."
        stretch
      >
        <SpaceBetween size="xxs">
          {coverage.lines.map((line) => {
            const lineKey = `${line.projectDocId}/${line.pPartDocId}`;
            const matchesLine = (lot) => lot
            && lot.projectDocId === line.projectDocId && lot.pPartDocId === line.pPartDocId;
            const savedEntries = installedLots
              .filter((entry) => matchesLine(savedLotById[entry.lotId]));
            const linePending = pendingLots.filter(matchesLine);
            // What the line's bags will hold after this save — below one
            // traveler's worth means the next traveler needs a new bag.
            const remainingAfter = linePending.reduce(
              (sum, lot) => sum + Math.max(lot.remainingQty ?? 0, 0)
              + (coverage.deltaByLotId[lot.id] || 0),
              0,
            ) + savedEntries.reduce(
              (sum, entry) => sum + Math.max(savedLotById[entry.lotId]?.remainingQty ?? 0, 0),
              0,
            );

            return (
              <div
                key={lineKey}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
                }}
              >
                <PPartRefBadge
                  projectDocId={line.projectDocId}
                  pPartDocId={line.pPartDocId}
                  quantity={line.fullQuantity}
                />
                {line.short > 0
                  ? <StatusIndicator type="warning">{`short by ${line.short}`}</StatusIndicator>
                  : <StatusIndicator type="success">covered</StatusIndicator>}
                {savedEntries.map((entry) => (
                  <Badge
                    key={entry.lotId}
                  // A bag quarantined after install turns its chip red so
                  // the operator sees the step is affected.
                    color={savedLotById[entry.lotId]?.status === LotStatus.QUARANTINED ? 'red' : 'grey'}
                  >
                    {`${savedLotById[entry.lotId]?.code || entry.lotId} ×${entry.quantity}`}
                  </Badge>
                ))}
                {linePending.map((lot) => (
                  <span key={lot.id} style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Badge color="blue">
                      {`${lot.code} ×${-(coverage.deltaByLotId[lot.id] || 0)}`}
                    </Badge>
                    <Button
                      iconName="close"
                      variant="inline-icon"
                      ariaLabel={`Remove ${lot.code}`}
                      onClick={() => onPendingChange(
                        (prev) => prev.filter((pendingLot) => pendingLot.id !== lot.id),
                      )}
                    />
                  </span>
                ))}
                {line.short === 0 && remainingAfter < line.fullQuantity && (
                <Box color="text-status-warning" fontSize="body-s">
                  less than one traveler&apos;s worth left — stage the next bag
                </Box>
                )}
              </div>
            );
          })}
          <ScanField
            id={`step-${stepIndex}-component-scan`}
            accept={[ScanTypes.LOT]}
            onScan={handleScan}
            placeholder="Scan or type a bag…"
          />
        </SpaceBetween>
      </FormField>
    </Container>
  );
}

StepComponentsField.propTypes = {
  stepIndex: PropTypes.number.isRequired,
  projectDocId: PropTypes.string,
  pPartDocId: PropTypes.string,
  installedLots: PropTypes.arrayOf(PropTypes.shape({
    lotId: PropTypes.string,
    quantity: PropTypes.number,
  })),
  // eslint-disable-next-line react/forbid-prop-types
  pendingLots: PropTypes.arrayOf(PropTypes.object).isRequired,
  onPendingChange: PropTypes.func.isRequired,
  onCoverageChange: PropTypes.func.isRequired,
};
